// Content for the Kai Coach marketing page (/kai-coach).
//
// The course transcripts are paraphrased from real sessions, except Physics,
// which is a design target written before its pilot; the footnotes say so.
// Product claims stay within what Kai Coach does today. The results chart and
// the measures table both read `pilotMeasures`, so they cannot disagree.

export type TranscriptSegment = string | { mark: string };

export interface TranscriptTurn {
  speaker: 'coach' | 'student';
  /** Replaces the speaker's name, e.g. "Student · first draft". */
  label?: string;
  segments: TranscriptSegment[];
  /** The number of the note that explains this turn. */
  note?: number;
}

export interface SessionNote {
  body: string;
  title: string;
}

export interface KaiCoachSession {
  designTarget?: string;
  id: 'ethics' | 'history' | 'physics';
  intro?: string;
  notes: SessionNote[];
  outcome: string;
  subtitle: string;
  tab: string;
  title: string;
  turns: TranscriptTurn[];
}

export interface PilotMeasure {
  after: number;
  before: number;
  /** Present on the measures the chart plots, all of them percentages. */
  chart?: { context: string; label: string };
  decimals: number;
  /** The one content measure that survived multiple-comparison correction. */
  heldUp?: boolean;
  id: string;
  label: string;
  unit: 'count' | 'percent';
}

export const heroExchange: TranscriptTurn[] = [
  {
    segments: [
      '…if a buyer values a unit more than it costs a seller to provide it, both could have gained from that trade. When the ceiling prevents it, what shared benefit is left unrealized?',
    ],
    speaker: 'coach',
  },
  { segments: ['I don’t know'], speaker: 'student' },
  {
    segments: [
      'No problem. The missing benefit is the gain both sides would have received from a trade worth more to the buyer than it cost the seller to make; the book calls that lost benefit ',
      { mark: '“surplus.”' },
      ' In your own words, ',
      { mark: 'does a binding ceiling mean fewer beneficial trades happen, or more?' },
    ],
    speaker: 'coach',
  },
];

export const proofStats = [
  { label: 'Developed arguments per paper', detail: 'from 1.0 to 2.0', value: '2×' },
  { label: 'Stated a clear position', detail: 'on the assigned book, up from 43%', value: '73%' },
  { label: 'Of final exams cited a specific reading', detail: 'up from 7%', value: '87%' },
  { label: 'Of coach turns asked a question', detail: 'across 389 sessions', value: '100%' },
] as const;

export const comparisonRows = [
  {
    chatbot: 'Writes the thesis, the paragraph or the whole essay when asked.',
    coach: 'Never writes a thesis, a sentence or a paragraph. It asks the next question.',
  },
  {
    chatbot: 'Improvises about a subject it half knows.',
    coach: 'Grounded in the book and readings your course assigns, so it points to real passages.',
  },
  {
    chatbot: 'Gives generic advice to a generic student.',
    coach: 'Designed around where your previous students actually stalled.',
  },
  {
    chatbot: 'Happens in another tab, out of the instructor’s sight.',
    coach: 'Part of the course: you can read every session and download it with scores.',
  },
] as const;

export const sessions: KaiCoachSession[] = [
  {
    id: 'history',
    intro:
      'History 2C students were asked to interpret a line from Walter Benjamin: “There is no document of civilization which is not at the same time a document of barbarism.” This student doesn’t start with a draft. They start by admitting the sentence isn’t clear yet.',
    notes: [
      {
        body: 'The student wants to understand the quote first. The coach breaks the sentence into pieces and asks about one.',
        title: 'It starts where the student is.',
      },
      {
        body: 'It defines the term, then hands the work back: pick something from this course and say who paid for it.',
        title: 'At the stall: a definition, nothing more.',
      },
      {
        body: 'The first draft is fluent and impossible to disagree with. The coach asks what Benjamin claims is always true, and what an opponent would say.',
        title: 'It names the gap, not the fix.',
      },
      {
        body: '“Shows” becomes “argues,” “always” makes the claim universal, and an objection is raised and answered.',
        title: 'The student writes the thesis.',
      },
    ],
    outcome: 'The coach named none of those three changes. The student made all of them.',
    subtitle: 'Recorded session · UCLA',
    tab: 'History 2C',
    title: 'From a first reaction to a real thesis, in three questions',
    turns: [
      { segments: ['I want to understand the quote before I write anything.'], speaker: 'student' },
      {
        note: 1,
        segments: [
          'Good place to start. Benjamin writes that there is no document of civilization which is not at the same time a document of barbarism. Take the sentence a piece at a time. What do you think he means by a document of civilization?',
        ],
        speaker: 'coach',
      },
      { segments: ['I don’t know'], speaker: 'student' },
      {
        note: 2,
        segments: [
          'No problem. ',
          {
            mark: 'A document of civilization is anything a culture keeps and celebrates as proof of its own achievement: an archive, a cathedral, a university, a canon of great books.',
          },
          ' Pick one we have looked at in this course. Who had to do what, and to whom, for that thing to exist?',
        ],
        speaker: 'coach',
      },
      {
        label: 'Student · first draft',
        segments: [
          'Benjamin’s statement shows that many of the achievements celebrated in history were built through inequality and suffering. Cities and cultural institutions represent civilization and progress, but they also show the labor and violence that made them possible. His idea forces us to look underneath the surface of historical events.',
        ],
        speaker: 'student',
      },
      {
        note: 3,
        segments: [
          'You are doing strong application work, but the paragraph is still closer to a reaction than to the claim Benjamin is actually making. You treat a monument of civilization as an achievement or an institution without saying what makes his assertion stronger and more arguable than “sometimes progress involves harm.” ',
          {
            mark: 'What exactly is he claiming is always true — and what would a reasonable person say if they wanted to disagree with him?',
          },
        ],
        speaker: 'coach',
      },
      {
        label: 'Student · revised',
        note: 4,
        segments: [
          'Benjamin ',
          { mark: 'argues' },
          ' that no document of civilization is not also a document of barbarism, which means that every achievement celebrated as progress rests on suffering or oppression usually hidden from view. ',
          { mark: 'Some would argue' },
          ' that civilization can exist without causing harm. Benjamin’s claim is that the exploitation is ',
          { mark: 'always' },
          ' part of its history, even when only historical investigation reveals it.',
        ],
        speaker: 'student',
      },
    ],
  },
  {
    id: 'ethics',
    intro:
      'A student in the Business Ethics pilot module asks to start with the basics of price controls. Ten turns later they reach the module’s ethical twist, by inference rather than by being told.',
    notes: [
      {
        body: 'One question at a time, starting from what the student asked for.',
        title: 'It builds from the ground up.',
      },
      {
        body: 'Each reply says what the student got right and asks for the next step in the causal chain.',
        title: 'It confirms, then asks for the next link.',
      },
      {
        body: 'The black market is plausible, so the coach says so, then steers back to who gets the scarce legal units.',
        title: 'It takes a detour seriously.',
      },
      {
        body: 'The student’s own answer, “chance,” is what breaks the textbook model’s assumption.',
        title: 'The twist arrives by inference.',
      },
    ],
    outcome:
      'Five questions. The coach never states the conclusion. It asks whether the textbook’s assumption survives what the student just said.',
    subtitle: 'Pilot module',
    tab: 'Business Ethics',
    title: 'From “the basics” to the ethical twist, in five questions',
    turns: [
      { segments: ['Let’s go over the basics'], speaker: 'student' },
      {
        note: 1,
        segments: [
          'Let’s build it from the ground up. Suppose a city caps the price of a good below what it would normally sell for — what happens to how much sellers offer and how much buyers want, and why?',
        ],
        speaker: 'coach',
      },
      { segments: ['Price lowers demand rises'], speaker: 'student' },
      {
        note: 2,
        segments: [
          'Yes — buyers want more at the lower price. What happens to sellers’ willingness to provide the good when they can receive less for it?',
        ],
        speaker: 'coach',
      },
      {
        segments: ['Willingness lessens and they shift focus to better margin goods'],
        speaker: 'student',
      },
      {
        segments: [
          'That’s the right causal picture: sellers may supply less or redirect their effort where returns cover their costs better. With more people trying to buy and fewer units available, what situation does that create, and who ends up without the good?',
        ],
        speaker: 'coach',
      },
      { segments: ['No a black market starts!'], speaker: 'student' },
      {
        note: 3,
        segments: [
          'A black market is a plausible response to the shortage, and it may mean that some goods are resold above the legal cap. But before that develops, consider the scarce units sold legally at the capped price: what is likely to determine which buyers get those — need, willingness to pay, or something else?',
        ],
        speaker: 'coach',
      },
      { segments: ['Chance'], speaker: 'student' },
      {
        note: 4,
        segments: [
          'Exactly — chance, or who is in the right place at the right time. ',
          {
            mark: 'The standard deadweight-loss triangle quietly assumes instead that the people shut out are the buyers who value the good least; given what you just said, does that assumption hold in a scramble?',
          },
        ],
        speaker: 'coach',
      },
    ],
  },
  {
    designTarget:
      'Written against the Fluid Mechanics chapter before the Fall 2026 Physics 1B pilot, to specify what the coach may do when a student stops.',
    id: 'physics',
    notes: [
      {
        body: 'The question comes from the reading the student was assigned.',
        title: 'It opens with the chapter’s own hook.',
      },
      {
        body: '“Force” is half right, so the coach supplies the other half and moves on.',
        title: 'It completes half an answer.',
      },
      {
        body: 'It names the pressure gradient and points to Section 7. The student still has to reason out what a wider spread would do.',
        title: 'On a stall: the name, never the inference.',
      },
      {
        body: 'The answer becomes the bridge to the chapter’s next case: the diver with the bends.',
        title: 'It hands back a harder question.',
      },
    ],
    outcome:
      'In a quantitative course, a stuck student is one step from a worked solution. So the rule is a prohibition: a name, a definition or a pointer to a section is allowed; a substitution, a rearrangement or a numerical result is not.',
    subtitle: 'Design target · Fall 2026',
    tab: 'Physics 1B',
    title: 'On a stall: the name, never the inference',
    turns: [
      {
        note: 1,
        segments: [
          'The chapter opens with a hurricane alert: atmospheric pressure has fallen to roughly 15 percent below average, and forecasters expect winds over 100 mph. Fifteen percent sounds small. Before we get to the weather, what is pressure measuring at a given point in the air?',
        ],
        speaker: 'coach',
      },
      { segments: ['force'], speaker: 'student' },
      {
        note: 2,
        segments: [
          'Force is half of it. ',
          { mark: 'Pressure is force per unit area' },
          ', which is part of why the number reads as small. Now take the difference between the pressure in the eye of the storm and the pressure a few hundred kilometers away. What would that difference do to the air sitting in between?',
        ],
        speaker: 'coach',
      },
      { segments: ['push it toward the middle'], speaker: 'student' },
      {
        segments: [
          'Yes, and that moving air is the wind. So the severity is not caused by the low pressure on its own, since a calm day at altitude has low pressure too. It has to be something about the difference. What feature of that difference decides how hard the air gets pushed?',
        ],
        speaker: 'coach',
      },
      { segments: ['I don’t know'], speaker: 'student' },
      {
        note: 3,
        segments: [
          'No problem. ',
          { mark: 'The quantity is the pressure gradient' },
          ', meaning how much the pressure changes over a given distance rather than how far it has fallen overall. ',
          { mark: 'Section 7' },
          ' develops the same idea for depth in a fluid. In your own words: if that same 15 percent drop were spread across the whole Atlantic instead of a few hundred kilometers, would the wind be stronger or weaker?',
        ],
        speaker: 'coach',
      },
      { segments: ['weaker'], speaker: 'student' },
      {
        note: 4,
        segments: [
          'Right, and that is the answer to the question the chapter opened with. Hold onto the gradient idea, because the diver with the bends is the same physics in a denser fluid. What does a diver’s ascent have in common with air flowing into the eye?',
        ],
        speaker: 'coach',
      },
    ],
  },
];

export const ruleMay = [
  'Name a concept or a term',
  'Give a definition',
  'Point to a section of the reading',
  'Say what the student got right',
] as const;

export const ruleNever = [
  'Write a thesis, a sentence or a paragraph',
  'Make the inference for the student',
  'Substitute, rearrange or produce a numerical result',
  'End a turn without a question',
] as const;

export const studyFacts = [
  { detail: '76 students in 2025, 56 in 2026', title: 'Two cohorts' },
  { detail: 'Instructor, prompts word for word, six-week structure', title: 'Same course design' },
  { detail: '745 discussion posts and 235 essays', title: '980 pieces of writing' },
  { detail: 'Measured from the submitted text', title: 'No grades used' },
] as const;

export const pilotMeasures: PilotMeasure[] = [
  {
    after: 73,
    before: 43,
    chart: { context: 'papers', label: 'Stated a clear position on the assigned book' },
    decimals: 0,
    id: 'clear-position',
    label: 'Stated a clear position on the assigned book',
    unit: 'percent',
  },
  {
    after: 2,
    before: 1,
    decimals: 1,
    id: 'argument-paragraphs',
    label: 'Developed argument paragraphs per paper',
    unit: 'count',
  },
  {
    after: 15.8,
    before: 34.7,
    chart: { context: 'papers · lower is better', label: 'No developed argument anywhere' },
    decimals: 1,
    id: 'no-argument',
    label: 'Papers with no developed argument anywhere',
    unit: 'percent',
  },
  {
    after: 14.7,
    before: 5,
    chart: { context: 'papers', label: 'Four or more developed arguments' },
    decimals: 1,
    id: 'four-or-more',
    label: 'Papers with four or more developed arguments',
    unit: 'percent',
  },
  {
    after: 4.99,
    before: 3.28,
    decimals: 2,
    heldUp: true,
    id: 'mechanism-claims',
    label: 'Mechanism claims per 1,000 words, final exam',
    unit: 'count',
  },
  {
    after: 87,
    before: 7,
    chart: { context: 'final exams', label: 'Cited a specific course-reader section' },
    decimals: 0,
    id: 'course-reader',
    label: 'Final exams citing a specific course-reader section',
    unit: 'percent',
  },
  {
    after: 0.18,
    before: 0.46,
    decimals: 2,
    id: 'machine-writing',
    label: 'Machine-writing markers per 1,000 words',
    unit: 'count',
  },
  {
    after: 51,
    before: 7,
    chart: { context: 'discussion posts', label: 'Traced a mechanism, by Week 5' },
    decimals: 0,
    id: 'mechanism-posts',
    label: 'Discussion posts tracing a mechanism, by Week 5',
    unit: 'percent',
  },
];

/** The chart's rows, top to bottom. */
export const chartMeasureIds = [
  'clear-position',
  'course-reader',
  'mechanism-posts',
  'four-or-more',
  'no-argument',
] as const;

/** The stat tiles beside the chart. */
export const tileMeasureIds = ['argument-paragraphs', 'mechanism-claims'] as const;

export function findMeasure(id: string): PilotMeasure {
  const measure = pilotMeasures.find((candidate) => candidate.id === id);

  if (!measure) {
    throw new Error(`Unknown pilot measure: ${id}`);
  }

  return measure;
}

export function formatMeasure(measure: PilotMeasure, value: number): string {
  const number = value.toFixed(measure.decimals);
  return measure.unit === 'percent' ? `${number}%` : number;
}

export const openings = [
  {
    caption: 'A truism, a cliché and a definition, before the question is touched.',
    quote:
      'History always has two sides: the main story filled with heroes and victors write what the future will read… Time and time again history repeats itself. Civilization is defined as an advancement of some sort for a group of people…',
    year: '2025 · No coach',
  },
  {
    caption: 'A causal mechanism, located in institutional authority, in the first sentence.',
    quote:
      'Heresy is defined less by doctrine than by who gets to speak for God; movements become ‘heretical’ when they bypass clerical control.',
    year: '2026 · Kai Coach',
  },
] as const;

export const modules = [
  {
    course: 'History 2C, UCLA · Summer 2026',
    detail:
      'Discussion posts, Paper 1, peer response and the final exam: four modules across a six-week term, built from the previous year’s student writing.',
    status: 'Run complete',
    tone: 'running',
    title: 'History',
  },
  {
    course: 'Physics 1B, UCLA, with AIMS',
    detail:
      'Post-exam reflection, pre-exam calibration and weekly probes. Physics adds answers with a marking key, so reasoning and accuracy can be measured together.',
    status: 'Pilot · Fall 2026',
    tone: 'pilot',
    title: 'Physics',
  },
  {
    course: 'Comparative Literature, UCLA',
    detail:
      'Interpretation, close reading and sustained revision, attached to what the course’s writing requirement already asks for. Its questions come from that course’s own student writing.',
    status: 'Winter 2027',
    tone: 'planned',
    title: 'Comparative Literature',
  },
  {
    course: 'Undergraduate pilot module',
    detail:
      'Coaches one argument about price controls through to the ethical judgment it forces: who goes without when a scarce good is rationed by queue instead of price.',
    status: 'In preparation',
    tone: 'planned',
    title: 'Business Ethics',
  },
] as const;

export const facultySteps = [
  {
    body: 'On a mapping call, we look at your prompts and a previous cohort’s writing to find where your students get stuck.',
    title: 'Show us where students stall',
  },
  {
    body: 'Kudu builds the module at no cost to your institution, from the book and readings you assign: a Kudu textbook, or the edition you already teach from.',
    title: 'We build it on your readings',
  },
  {
    body: 'Preview it as a student, set the dates and release it. In all, it takes about one to two hours of your time.',
    title: 'You review it and release it',
  },
] as const;

export const facultyControls = [
  'Private instructions the coach follows in every session',
  'Open and due dates for each session',
  'Focus areas students choose from',
  'Credit for taking part, or for reaching your goal',
  'Preview as a student before you release it',
  'Every conversation, downloadable with scores',
] as const;

export const leaderCards = [
  {
    body: 'Kai Coach is built not to do students’ work. In the History pilot, every coach turn across 389 sessions asked a question.',
    title: 'Integrity by design',
  },
  {
    body: 'A two-cohort comparison on real coursework, measured from submitted writing rather than grades. The full pilot report is available on request.',
    title: 'Evidence you can show',
  },
  {
    body: 'Papers with no developed argument fell by more than half, while papers with four or more roughly tripled.',
    title: 'Gains across the whole class',
  },
  {
    body: 'Student messages are anonymized before they reach a model provider, providers work under contractual no-training guarantees, and student data is never sold or shared.',
    title: 'The same privacy as KAI',
  },
] as const;

export const faqs = [
  {
    answer:
      'KAI is the course-aware AI in every Kudu course: students ask, and it guides them. Kai Coach is a structured module an instructor adds for a specific piece of work, such as discussion posts, a paper, peer response or exam preparation. It has its own sessions, dates and credit, and a stricter rule: every turn is a question.',
    question: 'How is Kai Coach different from KAI?',
  },
  {
    answer:
      'No. It is designed never to write a thesis, a sentence or a paragraph. When a student is stuck it may supply a term or a definition, or point to a section of the reading. Then it asks the next question.',
    question: 'Will it write an essay if a student insists?',
  },
  {
    answer:
      'Nothing for your institution. Kudu builds every module, and faculty spend about one to two hours reviewing and releasing it.',
    question: 'What does it cost, and how much of my time does it take?',
  },
  {
    answer:
      'No. Kai Coach is built on whatever your course assigns: a Kudu textbook where you use one, or the trade or university-press edition you already teach from. It also works as a standalone module in courses that don’t use Kudu.',
    question: 'Do I need a Kudu textbook, or Kudu at all?',
  },
  {
    answer:
      'Yes. You can read your students’ sessions with the coach and download them with scores, so the coaching is part of the course rather than something happening beside it.',
    question: 'Can I see what my students and the coach said?',
  },
  {
    answer:
      'The same way as in KAI: student messages are anonymized before they reach a model provider, providers work under contractual no-training guarantees, and we never sell or share student data.',
    question: 'How is student data handled?',
  },
] as const;
